import { useState } from 'react';
import { PILLARS, getScoreColor } from '../utils/scoring';
import TickSlider from './TickSlider';
import { supabase } from '../utils/supabase';

export default function TeamScoring({ audit, onUpdate }) {
  const [showPanel, setShowPanel] = useState(false);
  const teamScores = audit.teamScores || {};
  const scorers = Object.keys(teamScores);

  const addMyScores = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    const name = user?.user_metadata?.full_name || user?.email || 'Unknown';
    const userId = user?.id || 'anon';

    if (!teamScores[userId]) {
      const myScores = {};
      PILLARS.forEach(p => {
        myScores[p.id] = audit.scores?.[p.id]?.score || 0;
      });
      const updated = {
        ...teamScores,
        [userId]: { name, scores: myScores, timestamp: new Date().toISOString() },
      };
      onUpdate({ ...audit, teamScores: updated });
    }
    setShowPanel(true);
  };

  const updateMyScore = async (pillarId, value) => {
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id || 'anon';
    if (!teamScores[userId]) return;

    const updated = {
      ...teamScores,
      [userId]: {
        ...teamScores[userId],
        scores: { ...teamScores[userId].scores, [pillarId]: value },
        timestamp: new Date().toISOString(),
      },
    };
    onUpdate({ ...audit, teamScores: updated });
  };

  // Calculate consensus
  const getConsensus = (pillarId) => {
    const values = scorers.map(uid => teamScores[uid]?.scores?.[pillarId] || 0).filter(v => v > 0);
    if (values.length === 0) return { avg: 0, variance: 0, agree: true };
    const avg = values.reduce((s, v) => s + v, 0) / values.length;
    const variance = values.reduce((s, v) => s + Math.pow(v - avg, 2), 0) / values.length;
    return { avg: Math.round(avg * 10) / 10, variance: Math.round(variance * 10) / 10, agree: variance < 1 };
  };

  const applyConsensus = () => {
    const newScores = { ...audit.scores };
    PILLARS.forEach(p => {
      const c = getConsensus(p.id);
      if (c.avg > 0) {
        newScores[p.id] = { ...newScores[p.id], score: c.avg };
      }
    });
    onUpdate({ ...audit, scores: newScores });
  };

  return (
    <div className="card overflow-hidden mb-4">
      <button
        onClick={() => scorers.length > 0 ? setShowPanel(!showPanel) : addMyScores()}
        className="w-full flex items-center justify-between p-3 hover:bg-neutral-800/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm">👥</span>
          <span className="text-xs text-neutral-400">Team Scoring</span>
          {scorers.length > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-gold/20 text-brand-gold font-medium">{scorers.length} scorer{scorers.length !== 1 ? 's' : ''}</span>
          )}
        </div>
        <span className="text-neutral-600 text-xs">{showPanel ? '▲' : scorers.length > 0 ? '▼' : '+ Add My Scores'}</span>
      </button>

      {showPanel && (
        <div className="px-4 pb-4 border-t border-neutral-800 pt-3">
          {scorers.length === 0 ? (
            <button onClick={addMyScores} className="btn-primary text-xs w-full">Add My Independent Scores</button>
          ) : (
            <>
              {/* Scorer avatars */}
              <div className="flex gap-2 mb-4 flex-wrap">
                {scorers.map((uid, i) => {
                  const scorer = teamScores[uid];
                  const colors = ['#C9A668', '#f87171', '#60a5fa', '#c084fc', '#34d399'];
                  return (
                    <span key={uid} className="flex items-center gap-1.5 text-[11px] font-medium" style={{ color: colors[i % colors.length] }}>
                      <span className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] text-black font-bold" style={{ backgroundColor: colors[i % colors.length] }}>
                        {scorer.name.charAt(0).toUpperCase()}
                      </span>
                      {scorer.name.split(' ')[0]}
                    </span>
                  );
                })}
                {!scorers.some(async () => {
                  const { data: { user } } = await supabase.auth.getUser();
                  return user?.id;
                }) && (
                  <button onClick={addMyScores} className="text-[11px] px-2 py-1 rounded bg-neutral-800 text-neutral-400 hover:text-white">+ Add My Scores</button>
                )}
              </div>

              {/* Pillar comparison */}
              <div className="space-y-3">
                {PILLARS.map(p => {
                  const consensus = getConsensus(p.id);
                  const color = getScoreColor(consensus.avg);
                  return (
                    <div key={p.id} className={`rounded-lg p-3 ${consensus.agree ? 'bg-neutral-800/20' : 'bg-amber-500/5 border border-amber-500/20'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-neutral-300">{p.name}</span>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-bold ${color.text}`}>{consensus.avg.toFixed(1)}</span>
                          {!consensus.agree && <span className="text-[9px] text-amber-400">Disagree</span>}
                        </div>
                      </div>
                      {/* Individual scores */}
                      <div className="flex gap-3 mt-1">
                        {scorers.map((uid, i) => {
                          const s = teamScores[uid]?.scores?.[p.id] || 0;
                          const colors = ['#C9A668', '#f87171', '#60a5fa', '#c084fc', '#34d399'];
                          return (
                            <span key={uid} className="text-[10px] font-bold" style={{ color: colors[i % colors.length] }}>
                              {s.toFixed(1)}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Apply consensus */}
              <div className="mt-4 flex gap-2">
                <button onClick={applyConsensus} className="btn-primary text-xs flex-1">Apply Team Average as Final Scores</button>
              </div>

              {/* Disagreement summary */}
              {PILLARS.some(p => !getConsensus(p.id).agree) && (
                <div className="mt-3 bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
                  <p className="text-[10px] text-amber-400 font-semibold mb-1">Pillars with Disagreement:</p>
                  {PILLARS.filter(p => !getConsensus(p.id).agree).map(p => (
                    <p key={p.id} className="text-[10px] text-neutral-400">• {p.name} (variance: {getConsensus(p.id).variance})</p>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
